import { Component, type ReactNode, type ErrorInfo } from "react";
import { Link } from "wouter";
import { Sentry } from "@/lib/sentry";
import { queryClient } from "@/lib/queryClient";
import { isChunkLoadError } from "@/lib/lazyRetry";
import i18n from "@/i18n";

function BrokenDownCarIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 480 220"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <g stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M60 150 C60 150, 75 90, 100 75 C125 60, 160 55, 200 55 L300 55 C330 55, 355 65, 370 80 C385 95, 400 120, 420 150" />
        <path d="M30 150 L60 150" />
        <path d="M420 150 L450 150" />
        <path d="M30 150 C30 150, 25 155, 25 160 C25 168, 30 172, 30 172 L450 172 C450 172, 455 168, 455 160 C455 155, 450 150, 450 150" />
        <path d="M200 55 L200 85" />
        <path d="M260 55 L260 85" />
        <path d="M100 75 L100 110" />
        <path d="M60 110 L100 110 C100 110, 130 85, 200 85 L260 85 C330 85, 370 105, 400 120 L440 140" />
        <path d="M60 110 C50 115, 40 130, 35 140" />
        <path d="M440 140 C445 142, 448 146, 450 150" />
        <line x1="100" y1="110" x2="60" y2="110" />
        <path d="M140 110 L200 110" />
        <path d="M265 110 L340 110" />
        <path d="M200 85 L200 110" />
        <path d="M260 85 L260 110" />
        <rect x="42" y="125" width="25" height="12" rx="3" />
        <rect x="415" y="125" width="25" height="12" rx="3" />
      </g>
      <g stroke="currentColor" strokeWidth="2.5">
        <circle cx="130" cy="165" r="28" />
        <circle cx="130" cy="165" r="18" />
        <circle cx="130" cy="165" r="5" fill="currentColor" />
        <circle cx="380" cy="165" r="28" />
        <circle cx="380" cy="165" r="18" />
        <circle cx="380" cy="165" r="5" fill="currentColor" />
      </g>
      <g stroke="currentColor" strokeWidth="2">
        <line x1="30" y1="172" x2="102" y2="172" />
        <line x1="158" y1="172" x2="352" y2="172" />
        <line x1="408" y1="172" x2="450" y2="172" />
      </g>
      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.7">
        <path d="M250 48 C252 35, 248 25, 255 15" />
        <path d="M260 50 C265 38, 258 28, 268 18" />
        <path d="M270 48 C274 36, 270 26, 278 14" />
        <path d="M245 42 C240 30, 244 20, 238 10" />
      </g>
      <g stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.5">
        <path d="M200 42 L195 30 L205 32 L200 20" />
        <path d="M320 42 L325 28 L315 30 L320 18" />
      </g>
    </svg>
  );
}

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ErrorBoundary caught:", error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  render() {
    if (this.state.hasError) {
      // A failed code-split chunk cannot be recovered by a React state reset —
      // the module is missing from the bundle the page booted with. Offer a
      // true full-page reload that fetches the fresh deploy, with clear
      // "new version available" messaging instead of the generic 500 screen.
      if (isChunkLoadError(this.state.error)) {
        return (
          <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-16 text-center">
            <p className="font-sans text-2xl font-bold text-foreground sm:text-3xl">
              {i18n.t("errorBoundary.new_version_title")}
            </p>
            <p className="mt-3 max-w-md text-base text-muted-foreground">
              {i18n.t("errorBoundary.new_version_description")}
            </p>
            <div className="mt-8 flex gap-4">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                &#x21bb; {i18n.t("errorBoundary.reload")}
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-16 text-center">
          <div className="relative mb-2">
            <h1
              className="select-none font-sans text-[8rem] font-black leading-none tracking-tighter text-foreground sm:text-[12rem]"
              aria-label="500"
            >
              500
            </h1>
            <BrokenDownCarIllustration className="absolute bottom-[10%] left-1/2 w-[85%] -translate-x-1/2 text-foreground" />
          </div>

          <p className="mt-2 text-lg text-muted-foreground sm:text-xl">
            {i18n.t("errorBoundary.broken_down")}
          </p>
          <p className="mt-3 font-sans text-xl font-bold text-foreground sm:text-2xl">
            {i18n.t("errorBoundary.engine_trouble")}
          </p>

          {this.state.error && (
            <p className="mt-3 max-w-lg text-sm text-muted-foreground/70">
              {this.state.error.message}
            </p>
          )}

          <div className="mt-8 flex gap-4">
            <button
              type="button"
              onClick={() => {
                try {
                  queryClient.clear();
                } catch {
                  // best-effort: never let cache reset block recovery
                }
                this.setState({ hasError: false, error: null });
              }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-8 py-3.5 text-base font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              &#x21bb; {i18n.t("common.try_again")}
            </button>
            <Link
              href="/"
              onClick={() => {
                try {
                  queryClient.clear();
                } catch {
                  // best-effort: never let cache reset block recovery
                }
                this.setState({ hasError: false, error: null });
              }}
              className="inline-flex items-center gap-2 rounded-full border border-border px-8 py-3.5 text-base font-semibold text-foreground transition-colors hover:bg-muted"
            >
              &larr; {i18n.t("errorBoundary.go_home")}
            </Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
